/**
 * Смысловой блок карточки бренда (§7).
 *
 * Четыре блока — «На сайте», «Приоритет», «Продвижение», «Сервис White Glove
 * и заметки» — это та же мысленная модель, что в выборе колонок и в панели
 * фильтров. Порядок блоков фиксирован и от роли не зависит: когда Лена и
 * Кирилл обсуждают бренд по телефону, у них на экране одно и то же (§7).
 *
 * Здесь же живёт описание полей блоков (`CARD_BLOCKS`) и расчёты, которые по
 * нему делает карточка: заполнено/всего для свёрнутого блока, наличие правки
 * черновика внутри, пропуски зоны ответственности (§4.9).
 *
 * Правки уходят в черновик через стор и подсвечиваются как в таблице (§5.5):
 * полоса 2 px `--draft` слева, фон `--draft-bg`, строка «было: …» под
 * контролом — в карточке она видна всегда, а не только в тултипе.
 */
import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import type { Brand, ChannelCode, EnumValue } from '@shared/types.ts'
import {
  Badge, Button, Checkbox, ConsequenceLine, IconChevronDown, IconChevronRight,
  IconExternalLink, SegmentedControl, Select, TierBadge,
} from '../../../components/index.js'
import { brandCell, channelCell, type Cell, type Overlay } from '../../../lib/draft-overlay.js'
import { useApp } from '../../../store/app-store.js'
import { urlView } from '../table/values.js'
import {
  CommitInput, FieldsBlock, canEditValue, customFieldsFor, enumLabelOf,
  enumOptions, isEmptyValue, wasLine, type CardBlockId,
} from './FieldsBlock.js'
import { cardTexts } from './texts.card.js'
import './ChannelBlock.css'

// ──────────────────────────────── модель ────────────────────────────────────
/** Зона ответственности (§4.9): `roles.area`, новой сущности не вводим. */
export type CardArea = 'commerce' | 'seo' | 'marketing' | 'site'

export type CardFieldKind = 'present' | 'visibility' | 'tier' | 'url' | 'text' | 'enum' | 'flag'

export interface CardFieldDef {
  /** Код поля в `BrandChannelValues` или в `Brand`. */
  code: string
  label: string
  /** Канальное поле или брендовое (§P7: вселенная счётчика). */
  scope: 'channel' | 'brand'
  kind: CardFieldKind
  /** Справочник значений для kind = 'enum' | 'visibility' | 'tier'. */
  enumSet?: string
  /** Чья зона ответственности (§4.9). */
  area?: CardArea
}

export interface CardBlockDef {
  id: CardBlockId
  title: string
  /** Зоны, за которые отвечает блок: из них собирается «Отвечает …» (§7). */
  areas: CardArea[]
  fields: CardFieldDef[]
  /** Внутри блока есть категории — их рисует CategoriesBlock. */
  hasCategories?: boolean
}

/** Порядок блоков и полей — ровно как в §7, менять нельзя. */
export const CARD_BLOCKS: readonly CardBlockDef[] = [
  {
    id: 'site',
    title: cardTexts.blocks.site,
    areas: ['site'],
    fields: [
      {
        code: 'visibility', label: cardTexts.fields.visibility, scope: 'channel',
        kind: 'visibility', enumSet: 'visibility', area: 'commerce',
      },
      { code: 'url', label: cardTexts.fields.url, scope: 'channel', kind: 'url', area: 'site' },
    ],
  },
  {
    id: 'priority',
    title: cardTexts.blocks.priority,
    areas: ['commerce'],
    hasCategories: true,
    fields: [
      {
        code: 'tier', label: cardTexts.fields.tier, scope: 'channel',
        kind: 'tier', enumSet: 'tier', area: 'commerce',
      },
      {
        code: 'categoriesUnclear', label: cardTexts.fields.categoriesUnclear, scope: 'brand',
        kind: 'flag', area: 'site',
      },
    ],
  },
  {
    id: 'promo',
    title: cardTexts.blocks.promo,
    areas: ['seo', 'marketing'],
    fields: [
      {
        code: 'seoPriority', label: cardTexts.fields.seoPriority, scope: 'channel',
        kind: 'enum', enumSet: 'seo_priority', area: 'seo',
      },
      {
        code: 'contextPriority', label: cardTexts.fields.contextPriority, scope: 'channel',
        kind: 'enum', enumSet: 'context_priority', area: 'marketing',
      },
    ],
  },
  {
    id: 'service',
    title: cardTexts.blocks.service,
    areas: [],
    fields: [
      { code: 'delivery', label: cardTexts.fields.delivery, scope: 'channel', kind: 'text' },
      { code: 'installation', label: cardTexts.fields.installation, scope: 'channel', kind: 'text' },
      { code: 'warranty', label: cardTexts.fields.warranty, scope: 'channel', kind: 'text' },
      { code: 'note', label: cardTexts.fields.note, scope: 'channel', kind: 'text' },
    ],
  },
]

/** Канальные поля карточки — по ним считается расхождение каналов (§7). */
export const CHANNEL_VALUE_FIELDS: readonly CardFieldDef[] = CARD_BLOCKS
  .flatMap((block) => block.fields)
  .filter((field) => field.scope === 'channel' && field.kind !== 'present')

/** Значение поля блока с учётом черновика. */
export function fieldCell(
  overlay: Overlay, brand: Brand, channel: ChannelCode, field: CardFieldDef,
): Cell<unknown> {
  if (field.scope === 'brand') {
    return brandCell(overlay, brand, field.code === 'name' ? 'name' : 'categoriesUnclear')
  }
  return channelCell(overlay, brand, channel, field.code)
}

export interface BlockStats {
  filled: number
  total: number
  hasDraft: boolean
}

/**
 * Счётчик «(заполнено/всего)» на свёрнутом блоке и точка «есть правка» (§7).
 * Флаг «присутствие в канале» в знаменатель не идёт: это не пропуск, а признак.
 */
export function blockStats(
  block: CardBlockDef,
  brand: Brand,
  channel: ChannelCode,
  overlay: Overlay,
  customFieldsCount: number,
): BlockStats {
  let filled = 0
  let total = 0
  let hasDraft = false

  for (const field of block.fields) {
    const cell = fieldCell(overlay, brand, channel, field)
    if (cell.isDraft) hasDraft = true
    /* Признак «ведём в канале» и флаги в знаменатель не идут: снятый флаг — это
       не пропуск, а значение (П3, §4.9 — в «Пропущено» флаги тоже не считаются). */
    if (field.kind === 'present' || field.kind === 'flag') continue
    total += 1
    if (!isEmptyValue(cell.value)) filled += 1
  }

  if (block.hasCategories) {
    total += 1
    if (brand.categories.some((item) => !overlay.detached.has(item.id))) filled += 1
    if (brand.categories.some((item) => overlay.detached.has(item.id) || overlay.draftCategories.has(item.id))) {
      hasDraft = true
    }
  }

  total += customFieldsCount
  return { filled, total, hasDraft }
}

/** Пропуски зоны ответственности смотрящего по этому бренду и каналу (§4.9). */
export function missingForArea(
  brand: Brand, channel: ChannelCode, overlay: Overlay, area: string | null,
): CardFieldDef[] {
  if (!area || area === 'admin') return []
  return CARD_BLOCKS
    .flatMap((block) => block.fields)
    .filter((field) => field.area === area)
    .filter((field) => {
      const cell = fieldCell(overlay, brand, channel, field)
      if (field.kind === 'flag') return false
      return isEmptyValue(cell.value)
    })
}

/** «Отвечает Лена Усова» / «Отвечают Кирилл Горчатов и Гоша Терновский» (§7). */
export function ownersOfBlock(
  block: CardBlockDef, users: { area: string | null; shortName: string; isActive: boolean }[],
): string[] {
  return block.areas.flatMap((area) =>
    users.filter((user) => user.isActive && user.area === area).map((user) => user.shortName),
  )
}

/** Строгое расхождение каналов: оба значения заполнены и различны (§0, §7). */
export function channelsDiffer(brand: Brand, overlay: Overlay, a: ChannelCode, b: ChannelCode): boolean {
  if (!brand.channels[a]?.isPresent || !brand.channels[b]?.isPresent) return false
  return CHANNEL_VALUE_FIELDS.some((field) => {
    const left = fieldCell(overlay, brand, a, field).value
    const right = fieldCell(overlay, brand, b, field).value
    if (isEmptyValue(left) || isEmptyValue(right)) return false
    return left !== right
  })
}

/**
 * Адрес страницы человеческим видом: `high-level.ru/brands/barazza` — так он
 * написан в раскладке карточки (§7). Протокол и хвостовой слэш не несут
 * информации, у всех 178 адресов в данных хост один и тот же.
 */
/**
 * Адрес для показа. Один формат на всё приложение: карточка писала
 * `high-level.ru/brands/asko`, а таблица рядом — `/brands/asko`, и это
 * выглядело как два разных значения.
 */
export function prettyUrl(url: string): string {
  const view = urlView(url)
  if (!view.text) return url
  /* Кабинет подписан и здесь: «lk /brands/asko». */
  return view.site ? `${view.site} ${view.text}` : view.text
}

/** Нормализация адреса: кавычки, пробелы, протокол (§2.3 UrlInput). */
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim().replace(/^["'«]+|["'»]+$/g, '').replace(/\s+/g, '')
  if (trimmed === '') return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

export function isValidUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.hostname.includes('.')
  } catch {
    return false
  }
}

// ──────────────────────────────── компонент ──────────────────────────────────
export interface ChannelBlockProps {
  block: CardBlockDef
  brand: Brand
  channel: ChannelCode
  open: boolean
  onToggle: () => void
  /** Клик по подписи поля фильтрует таймлайн истории (§7). */
  onFilterField: (code: string, label: string) => void
  /** Бренд архивирован — карточка только на чтение. */
  readonly?: boolean
  /** Блок с категориями отдаёт их сюда. */
  children?: ReactNode
}

export function ChannelBlock({
  block, brand, channel, open, onToggle, onFilterField, readonly = false, children,
}: ChannelBlockProps) {
  const headId = useId()
  const bodyId = `${headId}-body`
  const overlay = useApp((state) => state.overlay)
  const bootstrap = useApp((state) => state.bootstrap)
  const users = bootstrap?.users ?? []
  const customCount = customFieldsFor(bootstrap?.fields, block.id).length
  const stats = blockStats(block, brand, channel, overlay, customCount)
  const owners = ownersOfBlock(block, users)

  return (
    <section className="card-block" aria-labelledby={headId}>
      <h3 className="card-block__head">
        <button
          type="button"
          id={headId}
          className="card-block__toggle t-h2"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <span className="card-block__chevron" aria-hidden="true">
            {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </span>
          <span className="card-block__title">{block.title}</span>
          {!open ? (
            <span
              className="card-block__count t-meta"
              aria-label={cardTexts.blocks.filledLabel(stats.filled, stats.total)}
            >
              {cardTexts.blocks.filled(stats.filled, stats.total)}
            </span>
          ) : null}
          {!open && stats.hasDraft ? (
            <Badge variant="draft" dot srLabel={cardTexts.blocks.hasDraft}>
              {''}
            </Badge>
          ) : null}
        </button>
      </h3>

      {owners.length > 0 ? (
        <p className="card-block__owner t-meta">{cardTexts.blocks.owner(owners)}</p>
      ) : null}

      <div id={bodyId} className="card-block__body" hidden={!open}>
        {block.fields.map((field) => (
          <CardFieldRow
            key={field.code}
            field={field}
            brand={brand}
            channel={channel}
            readonly={readonly}
            onFilterField={onFilterField}
          />
        ))}
        {children}
        <FieldsBlock brand={brand} channel={channel} blockId={block.id} readonly={readonly} />
      </div>
    </section>
  )
}

// ──────────────────────────────── строка поля ────────────────────────────────
interface CardFieldRowProps {
  field: CardFieldDef
  brand: Brand
  channel: ChannelCode
  readonly: boolean
  onFilterField: (code: string, label: string) => void
}

function CardFieldRow({ field, brand, channel, readonly, onFilterField }: CardFieldRowProps) {
  const controlId = useId()
  const overlay = useApp((state) => state.overlay)
  const bootstrap = useApp((state) => state.bootstrap)
  const editBrand = useApp((state) => state.editBrand)
  const editChannel = useApp((state) => state.editChannel)
  const user = bootstrap?.user ?? null
  const enums = bootstrap?.enums

  const cell = fieldCell(overlay, brand, channel, field)
  const permission = canEditValue(user, isEmptyValue(cell.published))
  const editable = permission.allowed && !readonly

  /* Поле «присутствие в канале» рисуется в блоке только когда бренд в канале
     есть: иначе весь блок подменяется заглушкой (§7). */
  if (field.kind === 'present' && cell.value !== true) return null

  function commitChannel(value: unknown): void {
    void editChannel(brand.id, channel, { [field.code]: value })
  }

  const rowClasses = ['card-field']
  if (cell.isDraft) rowClasses.push('card-field--draft')
  if (field.kind === 'flag') rowClasses.push('card-field--flag')

  const wasLabel = labelOfValue(field, cell.published, enums)
  const consequence = consequenceOf(field, cell.value, channel, bootstrap?.channels ?? [])

  return (
    <div className={rowClasses.join(' ')} data-field={field.code}>
      {/* Подпись поля — кнопка: клик по ней фильтрует таймлайн истории по
          этому полю (§7). Доступное имя контрола даёт сам контрол (aria-label),
          поэтому связь через <label for> здесь не нужна. */}
      {field.kind === 'flag' ? null : (
        <button
          type="button"
          className="card-field__label card-field__label--button t-label"
          onClick={() => onFilterField(field.code, field.label)}
        >
          {field.label}
          <span className="visually-hidden"> · {cardTexts.history.filterByField(field.label)}</span>
          <IconHistoryDot />
        </button>
      )}
      <div className="card-field__control">
        <FieldControl
          id={controlId}
          field={field}
          cell={cell}
          enums={enums}
          disabled={!editable}
          onCommitChannel={commitChannel}
          onCommitBrand={(value) => void editBrand(brand.id, { categoriesUnclear: value === true })}
        />
        {cell.isDraft ? (
          <span className="card-field__was t-meta">
            {wasLine(wasLabel)}
            <span className="visually-hidden"> · {cardTexts.value.draftHint}</span>
          </span>
        ) : null}
        {consequence ? <ConsequenceLine>{consequence}</ConsequenceLine> : null}
        {!editable && permission.reason ? (
          <span className="card-field__reason t-meta">{permission.reason}</span>
        ) : null}
      </div>
    </div>
  )
}

/** Часики у подписи поля: «эта правка есть в истории». Декоративная. */
function IconHistoryDot() {
  return (
    <svg className="icon" width="16" height="16" viewBox="0 0 20 20" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <path d="M10 5.5v4.5l3 1.8" />
      <circle cx="10" cy="10" r="6.25" />
    </svg>
  )
}

function labelOfValue(
  field: CardFieldDef, value: unknown, enums: Record<string, EnumValue[]> | undefined,
): string | null {
  if (isEmptyValue(value)) return null
  if (field.kind === 'flag' || field.kind === 'present') {
    return value === true ? cardTexts.value.yes : cardTexts.value.no
  }
  if (field.enumSet) return enumLabelOf(enums, field.enumSet, value)
  return String(value)
}

/** Строка-последствие под контролом (П6, §5.8). */
function consequenceOf(
  field: CardFieldDef,
  value: unknown,
  channel: ChannelCode,
  channels: { code: ChannelCode; name: string }[],
): string | null {
  if (field.code === 'visibility' && value === 'no') return cardTexts.consequence.visibilityNo
  if (field.kind === 'present' && value === false) {
    const name = channels.find((item) => item.code === channel)?.name ?? ''
    return cardTexts.consequence.presentOff(name)
  }
  return null
}

interface FieldControlProps {
  id: string
  field: CardFieldDef
  cell: Cell<unknown>
  enums: Record<string, EnumValue[]> | undefined
  disabled: boolean
  onCommitChannel: (value: unknown) => void
  onCommitBrand: (value: unknown) => void
}

function FieldControl({
  id, field, cell, enums, disabled, onCommitChannel, onCommitBrand,
}: FieldControlProps) {
  const [open, setOpen] = useState(false)

  switch (field.kind) {
    case 'present':
    case 'visibility': {
      const options = (enums?.[field.enumSet ?? 'visibility'] ?? [])
        .filter((item) => item.isActive)
        .map((item) => ({ value: item.value, label: item.label }))
      const yesNo = options.length > 0
        ? options
        : [{ value: 'yes', label: cardTexts.value.yes }, { value: 'no', label: cardTexts.value.no }]
      const current = field.kind === 'present'
        ? (cell.value === true ? 'yes' : 'no')
        : (typeof cell.value === 'string' ? cell.value : '')
      return (
        <SegmentedControl
          value={current}
          options={yesNo}
          label={field.label}
          onChange={(next) => {
            if (disabled) return
            if (field.kind === 'present') onCommitChannel(next === 'yes')
            else onCommitChannel(next)
          }}
          className={disabled ? 'card-field__segmented--disabled' : undefined}
        />
      )
    }

    /* Тир выбирается тем же списком, что и остальные справочники.
       Раньше это был ряд кнопок-радио: выбранная отличалась тонкой рамкой,
       и с полуметра было не понять, какая именно горит. Теперь в поле
       виден сам чип, а список открывается по нажатию — как везде. */
    case 'tier':
      return (
        <Select
          id={id}
          value={typeof cell.value === 'string' ? cell.value : null}
          options={enumOptions(enums, field.enumSet ?? 'tier')}
          onChange={(next) => onCommitChannel(next)}
          open={open}
          onOpenChange={setOpen}
          label={field.label}
          emptyLabel={cardTexts.value.empty}
          clearLabel={cardTexts.value.clear}
          disabled={disabled}
        />
      )

    case 'flag':
      return (
        <Checkbox
          id={id}
          checked={cell.value === true}
          onChange={(checked) => onCommitBrand(checked)}
          disabled={disabled}
          label={field.label}
        />
      )

    case 'url': {
      const value = typeof cell.value === 'string' ? cell.value : ''
      return (
        <UrlField
          inputId={id}
          value={value}
          label={field.label}
          disabled={disabled}
          onCommit={(next) => onCommitChannel(next)}
        />
      )
    }

    case 'enum':
      return (
        <Select
          id={id}
          value={typeof cell.value === 'string' ? cell.value : null}
          options={enumOptions(enums, field.enumSet ?? '')}
          onChange={(next) => onCommitChannel(next)}
          open={open}
          onOpenChange={setOpen}
          label={field.label}
          emptyLabel={cardTexts.value.empty}
          clearLabel={cardTexts.value.clear}
          disabled={disabled}
        />
      )

    case 'text':
    default:
      return (
        <CommitInput
          id={id}
          value={typeof cell.value === 'string' ? cell.value : ''}
          label={field.label}
          disabled={disabled}
          onCommit={(raw) => onCommitChannel(raw.trim() === '' ? null : raw.trim())}
        />
      )
  }
}

// ──────────────────────────────── тир ────────────────────────────────────────
export interface TierPickerProps {
  value: string | null
  /** Коды справочника тиров в порядке убывания веса. */
  options: string[]
  label: string
  disabled?: boolean
  onChange: (value: string | null) => void
}

/**
 * Выбор тира строкой бейджей `[A+] [A] [B] [C] [—]` (§7).
 *
 * Радиогруппа: выбран ровно один вариант, стрелки ходят по вариантам и
 * выбирают сразу — в отличие от канала, смена тира ничего не перестраивает.
 * Пустое значение — такой же вариант «—», потому что пустой тир это задача,
 * а не «тира нет по смыслу» (П3).
 */
export function TierPicker({ value, options, label, disabled = false, onChange }: TierPickerProps) {
  const items: (string | null)[] = [...options, null]
  const index = items.findIndex((item) => item === value)

  function move(direction: 1 | -1): void {
    if (disabled) return
    const from = index < 0 ? items.length - 1 : index
    const next = from + direction
    if (next < 0 || next > items.length - 1) return
    onChange(items[next] ?? null)
  }

  return (
    <div
      className="card-tier"
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault()
          event.stopPropagation()
          move(1)
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault()
          event.stopPropagation()
          move(-1)
        }
      }}
    >
      {items.map((item, position) => {
        const checked = item === value
        return (
          <button
            key={item ?? 'empty'}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={item ?? cardTexts.value.tierEmptyLabel}
            aria-disabled={disabled || undefined}
            tabIndex={checked || (index < 0 && position === items.length - 1) ? 0 : -1}
            className={checked ? 'card-tier__item card-tier__item--on' : 'card-tier__item'}
            onClick={() => {
              if (disabled) return
              onChange(item)
            }}
          >
            <TierBadge tier={item} />
          </button>
        )
      })}
    </div>
  )
}

// ──────────────────────────────── адрес страницы ─────────────────────────────
interface UrlFieldProps {
  inputId: string
  value: string
  label: string
  disabled: boolean
  onCommit: (value: string | null) => void
}

function UrlField({
  inputId, value, label, disabled, onCommit,
}: UrlFieldProps) {
  const [invalid, setInvalid] = useState<string | null>(null)

  return (
    <>
      <CommitInput
        id={inputId}
        value={value}
        label={label}
        disabled={disabled}
        validate={(raw) => {
          const normalized = normalizeUrl(raw)
          if (normalized === '' || isValidUrl(normalized)) return null
          return cardTexts.consequence.urlInvalid
        }}
        onCommit={(raw) => {
          const normalized = normalizeUrl(raw)
          if (normalized !== '' && !isValidUrl(normalized)) {
            setInvalid(raw.trim())
            return
          }
          setInvalid(null)
          onCommit(normalized === '' ? null : normalized)
        }}
      />
      {value !== '' && isValidUrl(value) ? (
        <a className="card-field__link t-meta" href={value} target="_blank" rel="noreferrer noopener">
          {prettyUrl(value)}
          <IconExternalLink size={16} />
          <span className="visually-hidden"> {cardTexts.value.openLink}</span>
        </a>
      ) : null}
      {/* Адрес не похож на адрес: говорим об этом и не трогаем значение —
          раньше тут предлагалось «положить в Примечание», но колонки
          «Примечание» больше нет. */}
      {invalid !== null ? (
        <ConsequenceLine>{cardTexts.consequence.urlInvalid}</ConsequenceLine>
      ) : null}
    </>
  )
}

/** Заглушка «бренд не ведётся в канале» (§7). */
export interface ChannelAbsentProps {
  brandName: string
  channelName: string
  otherChannelName: string
  canEdit: boolean
  onAdd: () => void
  onCopy: () => void
}

export function ChannelAbsent({
  brandName, channelName, otherChannelName, canEdit, onAdd, onCopy,
}: ChannelAbsentProps) {
  return (
    <div className="card-absent">
      <p className="card-absent__text t-body">{cardTexts.channels.absent(brandName, channelName)}</p>
      <div className="card-absent__actions">
        <Button variant="secondary" onClick={onAdd} disabled={!canEdit}>
          {cardTexts.channels.addTo(channelName)}
        </Button>
        <button
          type="button"
          className="card-absent__link t-meta"
          onClick={onCopy}
          disabled={!canEdit}
        >
          {cardTexts.channels.copyFrom(otherChannelName)}
        </button>
      </div>
      {!canEdit ? <ConsequenceLine>{cardTexts.deny.viewer}</ConsequenceLine> : null}
    </div>
  )
}
